import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import api from "../api/axios";
// UPDATED: Pointing to the Python Flask Server
const API_URL = '/api/chat';

const ChatBot = forwardRef((props, ref) => {
  // If embedded, it's always open. If not, it starts closed.
  const [isOpen, setIsOpen] = useState(props.embedded ? true : false);

  const [messages, setMessages] = useState([
    { text: "Hi there! I am MediBot, your AI medical assistant. How can I help you today?", sender: 'bot' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false); // State for loading indicator
  const messagesEndRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openChat: () => setIsOpen(true)
  }));

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll whenever messages change or the chat opens/closes
  useEffect(scrollToBottom, [messages, isOpen, isTyping]); 

  const handleSend = async (e) => {
    e.preventDefault();
    const userQuery = input.trim();
    if (!userQuery || isTyping) return;

    // 1. Add user message to state and clear input
    const userMessage = { text: userQuery, sender: 'user' };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true); // Start loading indicator

    try {
      // 2. Call the Python backend API
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userQuery }),
      });

      if (!response.ok) {
        // Attempt to parse error message from server response
        const errorData = await response.json().catch(() => ({ error: `HTTP error: ${response.status}` }));
        throw new Error(errorData.error || `Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      
      // 3. Add bot response to state
      const botResponse = {
        text: data.botResponse || "Sorry, I couldn't get a response from the AI.",
        sender: 'bot'
      };
      setMessages(prev => [...prev, botResponse]);

    } catch (error) {
      console.error("Failed to send message:", error);
      const errorMessage = {
        text: `Oops! There was an error connecting to the MediBot service. Is the Python backend running on port 5030?`,
        sender: 'bot'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };


  // --- DYNAMIC STYLES ---
 
  const containerStyle = props.embedded ? {
    position: 'relative',
    width: '100%',
    height: '100%',
    borderRadius: '20px',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#f8f9fa',
    overflow: 'hidden'
  } : {

    position: 'fixed',
    bottom: '100px',
    right: '30px',
    width: '400px', 
    height: '550px', 
    borderRadius: '25px',
    zIndex: 1000,
    animation: 'slideUp 0.3s ease-out',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#f8f9fa'
  };

  return (
    <>
    
      <link 
        rel="stylesheet" 
        href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css" 
      />

      {/* --- FLOATING BUTTON (Only show if NOT embedded) --- */}
      {!props.embedded && (
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="shadow-xl"
          style={{
            position: 'fixed',
            bottom: '30px',
            right: '30px',
            width: '60px',
            height: '60px',
            backgroundColor: '#282561',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1000,
            transition: 'transform 0.3s, box-shadow 0.3s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          {isOpen ? <i className="bi bi-x-lg text-white fs-4"></i> : <i className="bi bi-chat-dots-fill text-white fs-3"></i>}
        </div>
      )}

      {/* --- CHAT WINDOW --- */}
      {isOpen && (
        <div
          className={`card border-0 shadow-2xl overflow-hidden chatbot-container${props.embedded ? 'embedded' : ''}`}
          style={containerStyle}
        >
          {/* --- LEFT SIDEBAR (Blue) */}
          <div
            className="d-none d-md-flex flex-column justify-content-center align-items-center text-center p-4 text-white"
            style={{
              width: '40%', 
              background: 'linear-gradient(90deg, rgb(65 47 128) 0%, rgb(40, 37, 97) 100%)',
              borderRadius: '25px 0 0 25px' 
            }}
          >
            <div className="bg-white rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '70px', height: '70px' }}>
              <img src="https://cdn-icons-png.flaticon.com/512/4712/4712027.png" alt="Bot" width="40" />
            </div>
            <h3 className="fw-bold mb-2">MedBot</h3>
            <p className="small opacity-75 mb-4">
              Ask me anything about health symptoms.
            </p>
            <span className="badge bg-white text-primary rounded-pill px-4 py-2 fw-bold">
              24/7 SUPPORT
            </span>
          </div>

          {/* --- RIGHT CHAT --- */}
          
          <div className="d-flex flex-column" style={{ width: props.embedded ? '100%' : '60%', flex: 1 }}>

            {/* Messages List */}
            <div className="flex-grow-1 p-4 overflow-auto" style={{ backgroundColor: '#fff' }}>
              {messages.map((msg, index) => (
                <div key={index} className={`d-flex mb-3 ${msg.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                  <div
                    className={`p-3 shadow-sm`}
                    style={{
                      maxWidth: '90%',
                      backgroundColor: msg.sender === 'user' ? '#282561' : '#f0f0f0', // Lighter background for bot
                      color: msg.sender === 'user' ? '#fff' : '#333',
                      border: msg.sender === 'user' ? 'none' : '1px solid #ddd',
                      borderRadius: '12px',
                      fontSize: '0.9rem'
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              
             
              {isTyping && (
                <div className="d-flex mb-3 justify-content-start">
                  <div
                    className="p-3 shadow-sm bot-typing-indicator"
                    style={{
                      maxWidth: '70px',
                      backgroundColor: '#f0f0f0',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '12px',
                      fontSize: '0.9rem'
                    }}
                  >
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

           
            <div className="p-3 bg-white border-top">
              <form onSubmit={handleSend} className="d-flex align-items-center gap-2 bg-light rounded-pill px-2 py-1 border">
                <input
                  type="text"
                  className="form-control border-0 bg-transparent shadow-none"
                  placeholder={isTyping ? "MediBot is thinking..." : "Type your health question..."}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={isTyping}
                  style={{ fontSize: '0.95rem', paddingLeft: '15px' }}
                />
                <button
                  type="submit"
                  className="btn rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                  style={{ width: '40px', height: '40px', backgroundColor: '#282561', border: 'none', flexShrink: 0, opacity: isTyping || !input.trim() ? 0.5 : 1, transition: 'opacity 0.2s' }}
                  disabled={isTyping || !input.trim()}
                >
                  <i className="bi bi-send-fill text-white fs-6" style={{ transform: 'rotate(45deg)', marginLeft: '-2px', marginTop: '2px' }}></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        /* Typing Indicator Styles */
        @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1.0); }
        }
        .bot-typing-indicator {
            display: flex;
            align-items: center;
            padding: 8px 12px !important;
        }
        .bot-typing-indicator .dot {
            width: 8px;
            height: 8px;
            margin: 0 2px;
            background-color: #555;
            border-radius: 50%;
            display: inline-block;
            animation: bounce 1.4s infinite ease-in-out both;
        }
        .bot-typing-indicator .dot:nth-child(1) { animation-delay: -0.32s; }
        .bot-typing-indicator .dot:nth-child(2) { animation-delay: -0.16s; }
        .bot-typing-indicator .dot:nth-child(3) { animation-delay: 0s; }

        /* Mobile Responsive Overrides */
        @media (max-width: 768px) {
            .chatbot-container:not(.embedded) {
                flex-direction: column !important;
                /* Take up more screen space on small devices */
                // position: fixed !important;
                bottom: 0 !important;
                right: 0 !important;
                width: 100% !important;
                height: 100% !important;
                max-height: 100vh;
                border-radius: 0 !important;
            }
            /* Ensure the chat window takes full width/height on mobile */
            .chatbot-container > div:last-child {
                width: 100% !important;
            }
        }
      `}</style>
    </>
  );
});

export default ChatBot;


